import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { CATEGORIES, formatPrice, getDiscount } from "../../data/courses";
import { StarRating, Badge, Button } from "../../components/ui/UI";
import CourseThumbnail from "../../components/ui/CourseThumbnail";
import CourseCard from "../../components/ui/CourseCard";
import styles from "./CoursePage.module.css";
import CategoryIcon from "../../components/ui/CategoryIcon";

const TAG_COLORS = { BESTSELLER: "gold", HOT: "red", NEW: "green", POPULAR: "blue" };

export default function CoursePage() {
  const { id } = useParams();
  const { courses, addToCart, cart } = useApp();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [id]);

  const course = courses.find((c) => c.id === Number(id));
  if (!course) return (
    <div className={styles.notFound}>
      <h2>Course not found.</h2>
      <Link to="/category/all">← Back to all courses</Link>
    </div>
  );

  const related = courses.filter((c) => c.category === course.category && c.id !== course.id).slice(0, 3);
  const inCart  = cart.some((c) => c.id === course.id);
  const disc    = getDiscount(course.originalPrice, course.price);
  const category = CATEGORIES.find((c) => c.id === course.category);

  const handleBuyNow = () => {
    if (!inCart) addToCart(course);
    navigate("/checkout");
  };

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbBar}>
        <div className="container">
          <div className={styles.breadcrumb}>
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to={`/category/${course.category}`}>{category?.label}</Link>
            <span>›</span>
            <span>{course.title}</span>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>

          {/* ── Main column ── */}
          <div className={styles.main}>

            {/* Thumbnail hero */}
            <div className={styles.thumbWrap}>
              <CourseThumbnail thumbnail={course.thumbnail} title={course.title} size="hero" />
            </div>

            {/* Title block */}
            <div className={styles.titleBlock}>
              <div className={styles.tagsRow}>
                {course.tag && <Badge color={TAG_COLORS[course.tag] || "accent"}>{course.tag}</Badge>}
                <Badge color="accent"><CategoryIcon icon={category?.icon} size={14} /> {category?.label}</Badge>
                <Badge color="green">{disc}% OFF</Badge>
              </div>
              <h1 className={styles.courseTitle}>{course.title}</h1>
              <p className={styles.courseSubtitle}>{course.subtitle}</p>
              <div className={styles.courseMeta}>
                <StarRating rating={course.rating} count={course.reviews} />
                <span className={styles.metaDot}>·</span>
                <span className={styles.metaItem}>📄 {course.pages} pages</span>
                <span className={styles.metaDot}>·</span>
                <span className={styles.metaItem}>🔒 Password protected</span>
                <span className={styles.metaDot}>·</span>
                <span className={styles.metaItem}>⚡ Instant delivery</span>
              </div>
            </div>

            {/* Description */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>About This Course</h2>
              <p className={styles.description}>{course.description}</p>
            </div>

            {/* What you get */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>What You Get</h2>
              <div className={styles.getGrid}>
                {course.whatYouGet?.map((item, i) => (
                  <div key={i} className={styles.getItem}>
                    <span className={styles.getCheck}>✓</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Topics */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Topics Covered</h2>
              <div className={styles.topicsGrid}>
                {course.topics.map((t, i) => (
                  <div key={i} className={styles.topicPill}>
                    <span className={styles.topicDot} style={{ background: course.accentColor }} />
                    {t}
                  </div>
                ))}
              </div>
            </div>

            {/* DRM notice */}
            <div className={styles.drmNotice}>
              <span>🔒</span>
              <div>
                <strong>Protected PDF</strong>
                <p>This PDF is password-locked and tied to your email. It cannot be shared, printed, or forwarded. The unique password is emailed to you after purchase.</p>
              </div>
            </div>

            {/* Mobile buy box */}
            <div className={styles.mobileBuyBox}>
              <div className={styles.mobilePricing}>
                <span className={styles.bigPrice}>{formatPrice(course.price)}</span>
                <span className={styles.strikePrice}>{formatPrice(course.originalPrice)}</span>
                <Badge color="green">{disc}% OFF</Badge>
              </div>
              <Button variant="primary" size="lg" fullWidth onClick={handleBuyNow}>
                Buy Now — {formatPrice(course.price)}
              </Button>
              <Button variant="outline" size="md" fullWidth
                onClick={() => { if (!inCart) addToCart(course); }}>
                {inCart ? "✓ Added to Cart" : "+ Add to Cart"}
              </Button>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Related Courses</h2>
                <div className="grid-3">
                  {related.map((c, i) => (
                    <CourseCard key={c.id} course={c} delay={i * 80} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Sticky sidebar ── */}
          <div className={styles.sidebar}>
            <div className={styles.buyBox}>
              {/* Thumbnail mini */}
              <CourseThumbnail thumbnail={course.thumbnail} title={course.title} size="card" />

              <div className={styles.buyBoxBody}>
                <div className={styles.buyPricing}>
                  <span className={styles.bigPrice}>{formatPrice(course.price)}</span>
                  <div>
                    <span className={styles.strikePrice}>{formatPrice(course.originalPrice)}</span>
                    <Badge color="green">{disc}% OFF</Badge>
                  </div>
                </div>

                <Button variant="primary" size="lg" fullWidth onClick={handleBuyNow}
                  style={{ marginTop: 16 }}>
                  ⚡ Buy Now
                </Button>
                <Button variant="outline" size="md" fullWidth
                  onClick={() => { if (!inCart) addToCart(course); }}
                  style={{ marginTop: 8 }}>
                  {inCart ? "✓ In Cart" : "+ Add to Cart"}
                </Button>

                <div className={styles.buyFeatures}>
                  {[
                    ["📄", `${course.pages}-page PDF guide`],
                    ["⚡", "Instant email delivery"],
                    ["🔒", "Password-protected PDF"],
                    ["♾️", "Lifetime access"],
                    ["🔄", "7-day refund guarantee"],
                  ].map(([icon, text], i) => (
                    <div key={i} className={styles.buyFeature}>
                      <span>{icon}</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
